import getMySipUri from '../../presenceManager/getMySipUri';
import { getStore } from '../store/ucmaStore';
import { ChatMessageType } from '../store/schema/ChatMessageViewState';
import ChatState from '../store/schema/ChatState';
import type ChatViewState from '../store/schema/ChatViewState';
import { logUsage } from 'owa-analytics';
import type UserActivityType from 'owa-service/lib/contract/UserActivityType';
import { action, mutatorAction } from 'satcheljs';

export const setPendingMessage = mutatorAction(
    'UCMA_SET_PENDING_MESSAGE',
    (chat: ChatViewState, message: string) => {
        chat.pendingMessage = message;
    }
);

export const updateSessionId = mutatorAction(
    'UCMA_UPDATE_SESSION_ID',
    (chat: ChatViewState, sessionId: number) => {
        chat.sessionId = sessionId;
    }
);

export const addChat = action('UCMA_ADD_CHAT', (chat: ChatViewState) => ({ chat }));

export const addChatMessage = mutatorAction(
    'UCMA_ADD_CHAT_MESSAGE',
    (chat: ChatViewState, messageType: ChatMessageType, fromSipUri: string, message?: string) => {
        // First, check if this is the same message with the previous one we got from notification toast
        // This is not always accurate, especially when sender initials a conversatioin and sends two identical messages
        // but receiver hasn't respond to the first one. In that case receiver will only see one.
        // But that is already better to get two duplicated message which is not sent be sender.
        if (messageType == ChatMessageType.InstantMessage) {
            const lastMessage = chat.messages[chat.messages.length - 1];

            if (
                lastMessage &&
                lastMessage.messageType == ChatMessageType.InstantMessageFromToast &&
                lastMessage.fromSipUri == fromSipUri &&
                lastMessage.message == message
            ) {
                return;
            }
        }

        let sequence: number;

        if (
            messageType == ChatMessageType.InstantMessage ||
            messageType == ChatMessageType.InstantMessageFromToast
        ) {
            sequence = ++chat.lastMessageSequence;
            // Only change the lastReadMessageSequence when the side pane is shown and
            // the current chat is active.
            if (chat.state == ChatState.Normal) {
                chat.lastReadMessageSequence = sequence;
            }
        } else {
            sequence = ++chat.lastNotificationSequence;
        }

        chat.messages.push({
            sequence,
            messageType,
            fromSipUri,
            message,
            timestamp: Date.now(),
        });

        logUsage('UcmaSkypeAddMessage', [
            fromSipUri == getMySipUri(),
            chat.participants.length < 23 ? chat.participants.length : 23,
        ]);
    }
);

export const updateParticipantDisplayName = mutatorAction(
    'UCMA_UPDATE_PARTICIPANT_DISPLAY_NAME',
    (sipUri: string, displayName: string) => {
        getStore().participantCache.set(sipUri, displayName);
    }
);

export const removeChat = mutatorAction('UCMA_REMOVE_CHAT', (chat: ChatViewState) => {
    const { chats } = getStore();
    const index = chats.indexOf(chat);
    if (index >= 0) {
        chats.splice(index, 1);
    }
});

export const setUserActivity = mutatorAction(
    'UCMA_SET_USER_ACTIVITY',
    (chat: ChatViewState, activityType: UserActivityType, sipUri: string) => {
        chat.userActivity = {
            type: activityType,
            linkedUserSip: sipUri,
        };
    }
);

export const addParticipant = mutatorAction(
    'UCMA_ADD_RECIPIENT',
    (chat: ChatViewState, sipUri: string) => {
        chat.participants.push(sipUri);
    }
);

export const removeParticipant = mutatorAction(
    'UCMA_REMOVE_RECIPIENT',
    (chat: ChatViewState, sipUri: string) => {
        const index = chat.participants.indexOf(sipUri);
        if (index >= 0) {
            chat.participants.splice(index, 1);
        }
    }
);

export const setChatState = action(
    'UCMA_SET_CHAT_STATE',
    (chat: ChatViewState, state: ChatState) => ({ chat, state })
);

export const setIsMoreChatMenuShown = mutatorAction(
    'UCMA_SET_IS_MORE_CHAT_MENU_SHOWN',
    (isShown: boolean) => {
        getStore().isMoreChatMenuShown = isShown;
    }
);
